<template>
    <div>
        <!-- Intent Dialog - Shows BEFORE the main form -->
        <v-dialog v-model="showIntentDialog" persistent max-width="600px">
            <v-card>
                <v-card-title class="primary darken-2 white--text">
                    <v-layout align-center>
                        <v-btn icon dark @click="goBack" class="mr-2">
                            <v-icon>arrow_back</v-icon>
                        </v-btn>
                        <span class="headline">Tell Us About Your Campaign</span>
                    </v-layout>
                </v-card-title>

                <v-card-text class="pt-4">
                    <p class="subheading mb-3">
                        To help us better understand your savings goals and provide personalized recommendations,
                        please describe what you're saving for and why this campaign is important to you.
                    </p>

                    <v-textarea
                        v-model="campaignIntent"
                        label="What are you saving for? Why is this important to you?"
                        placeholder="Example: I want to save for a new laptop to improve my productivity at work. It will help me work more efficiently and learn new skills."
                        rows="4"
                        auto-grow
                        outlined
                        color="teal"
                        :rules="[v => !!v || 'Please describe your campaign purpose']"
                        counter
                        maxlength="500"
                    ></v-textarea>

                    <v-alert
                        :value="true"
                        type="info"
                        color="teal lighten-4"
                        icon="info"
                        class="mt-2"
                    >
                        <small>
                            This information helps our managers understand user preferences and
                            suggest better savings strategies tailored to your needs.
                        </small>
                    </v-alert>
                </v-card-text>

                <v-card-actions class="px-4 pb-4">
                    <v-spacer></v-spacer>
                    <v-btn
                        color="teal darken-2"
                        class="white--text px-4"
                        :disabled="!campaignIntent || campaignIntent.trim().length < 10"
                        @click="proceedToForm"
                    >
                        Continue
                        <v-icon right>arrow_forward</v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Main Campaign Form - Shows AFTER intent dialog -->
        <div v-show="!showIntentDialog">
            <h2 class="primary darken-2 white--text font-weight-bold subheading py-2 text-xs-center">
                CREATE NEW CAMPAIGN
            </h2>
            <v-divider></v-divider>
            <v-card color="white pa-3">
                <p class="dark--text pa-0 ma-0 text-xs-center">
                    With Campaigns you save with attainable objectives.
                </p>
                <v-layout wrap row>
                    <v-flex xs12 md10 offset-md1>
                        <v-layout row>
                            <v-flex xs2 sm1>
                                <i class="teal--text fas fa-piggy-bank fa-2x mt-3"></i>
                            </v-flex>
                            <v-flex xs9 sm10>
                                <v-text-field
                                    class="pa-0 mt-3"
                                    ref="campaign_name"
                                    v-model="campaignName"
                                    label="Enter Campaign Name"
                                    :rules="[v => !!v || 'Campaign Name is required']"
                                    color="teal"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs2>
                                <v-btn
                                    @click.stop="$store.commit('activateDialog', true); campaign_name_help = true; getHelpMessage()"
                                    flat fab small outline color="black"
                                    class="my-4"
                                    style="height:20px; width: 20px"
                                >
                                    <i class="fas fa-question"></i>
                                </v-btn>
                            </v-flex>
                        </v-layout>
                    </v-flex>

                    <v-flex xs12 md10 offset-md1>
                        <v-layout row>
                            <v-flex xs2 sm1>
                                <i class="teal--text fas fa-tag fa-2x mt-3"></i>
                            </v-flex>
                            <v-flex xs9 sm10>
                                <v-select
                                    @change="OnChangeCampaignType()"
                                    class="pt-0 mt-3"
                                    color="teal"
                                    v-model="campaignType"
                                    ref="campaign_type"
                                    :items="campaignTypes"
                                    :rules="[v => !!v || 'Campaign Types is required']"
                                    label="Select Campaign Types"
                                    required
                                ></v-select>
                            </v-flex>
                            <v-flex xs2>
                                <v-btn
                                    @click.stop="$store.commit('activateDialog', true); campaign_type_help = true; getHelpMessage()"
                                    flat fab small outline color="black"
                                    class="my-4"
                                    style="height:20px; width: 20px"
                                >
                                    <i class="fas fa-question"></i>
                                </v-btn>
                            </v-flex>
                        </v-layout>
                    </v-flex>

                    <v-flex xs12 md10 offset-md1>
                        <v-layout row>
                            <v-flex xs2 sm1>
                                <i class="teal--text fas fa-money-bill fa-2x mt-3"></i>
                            </v-flex>
                            <v-flex xs9 sm10>
                                <v-text-field
                                    class="pa-0 mt-3"
                                    ref="campaign_target_amount"
                                    label="Campaign Amount"
                                    v-model="campaignTargetAmount"
                                    :disabled='isAmountDisabled'
                                    :required='isAmountRequired'
                                    color="teal"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs2>
                                <v-btn
                                    @click.stop="$store.commit('activateDialog', true); campaign_amount_help = true; getHelpMessage()"
                                    flat fab small outline color="black"
                                    class="my-4"
                                    style="height:20px; width: 20px"
                                >
                                    <i class="fas fa-question"></i>
                                </v-btn>
                            </v-flex>
                        </v-layout>
                    </v-flex>

                    <v-flex xs12 md10 offset-md1>
                        <v-layout row>
                            <v-flex xs2 sm1>
                                <i class="teal--text fas fa-calendar fa-2x mt-3"></i>
                            </v-flex>
                            <v-flex xs9 sm10>
                                <v-text-field
                                    class="pa-0 mt-3"
                                    ref="campaign_target_date"
                                    label="Campaign Date"
                                    v-model="campaignTargetDate"
                                    :disabled='isDateDisabled'
                                    :required='isDateRequired'
                                    type="date"
                                    color="teal"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs2>
                                <v-btn
                                    @click.stop="$store.commit('activateDialog', true); campaign_date_help = true; getHelpMessage()"
                                    flat fab small outline color="black"
                                    class="my-4"
                                    style="height:20px; width: 20px"
                                >
                                    <i class="fas fa-question"></i>
                                </v-btn>
                            </v-flex>
                        </v-layout>
                    </v-flex>

                    <v-flex xs12 md10 offset-md1>
                        <v-layout row>
                            <v-flex xs2 sm1>
                                <i class="teal--text fas fa-pen-to-square fa-2x mt-3"></i>
                            </v-flex>
                            <v-flex xs9 sm10>
                                <v-textarea
                                    class="pa-0 mt-3"
                                    name="input-7-1"
                                    auto-grow
                                    v-model="campaignDescription"
                                    ref="campaign_description"
                                    label="Campaign Description"
                                    :rules="[v => !!v || 'Campaign Description is required']"
                                    background-color="grey lighten-4"
                                    color="teal"
                                    rows="2"
                                    box
                                    row-height="20"
                                    readonly
                                    hint="This was auto-filled from your campaign purpose"
                                ></v-textarea>
                            </v-flex>
                            <v-flex xs2>
                                <v-btn
                                    @click.stop="$store.commit('activateDialog', true); campaign_description_help = true; getHelpMessage()"
                                    flat fab small outline color="black"
                                    class="my-4"
                                    style="height:20px; width: 20px"
                                >
                                    <i class="fas fa-question"></i>
                                </v-btn>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>

                <div class="text-xs-center">
                    <v-btn
                        @click="createNewCampaign()"
                        class="white--text elevation-6"
                        type="submit"
                        small
                        round
                        color="teal"
                    >
                        <span>Submit</span>
                    </v-btn>
                </div>

                <!-- Welcome Dialog (original) -->
                <v-dialog width="500px" v-model="Dialog">
                    <v-card>
                        <v-card-title class="headline primary darken-2 white--text" primary-title>
                            Welcome To PettyCash
                        </v-card-title>
                        <v-card-text class="pa-2">
                            <p class="mb-0">
                                In order to start using our services, you will create your very first savings campaign
                            </p>
                            <div>
                                <p class="mb-0">
                                    Note that we have 3 campaign types (Money based, Time based and Money-Time based).
                                </p>
                                <p class="pl-2 mb-0 pt-1">
                                    - Money Based campaigns have money as their savings target with minimum being 25,000 F CFA
                                </p>
                                <p class="pl-2 mb-0 pt-1">
                                    - Time Based campaigns have time as their savings target with minimum being 1 month
                                </p>
                                <p class="pl-2 mb-0 pt-1">
                                    - Click <b class="teal--text darken-2">Start Now</b> below to begin
                                </p>
                            </div>
                        </v-card-text>
                        <v-card-actions class="px-3 text-xs-right">
                            <v-spacer></v-spacer>
                            <v-btn
                                color="teal darken-2"
                                class="white--text"
                                @click="Dialog = false"
                            >
                                Start Now
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-card>
        </div>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    data() {
        return {
            // Intent Dialog
            showIntentDialog: true,
            campaignIntent: "",

            // Original fields
            isAmountDisabled: false,
            isDateDisabled: false,
            isAmountRequired: false,
            isDateRequired: false,
            campaign_name_help: false,
            campaign_type_help: false,
            campaign_amount_help: false,
            campaign_date_help: false,
            campaign_description_help: false,
            campaignName: "",
            campaignType: "",
            campaignTargetAmount: "",
            campaignTargetDate: "",
            campaignDescription: "",
            campaignTypes: [],
            Dialog: false,
        }
    },

    mounted() {
        this.getCampaignTypes();
        this.getMarketItemInfo();

        this.$store.commit('setCloseURL', "/savings/campaigns/active")
        document.title = "PettyCash | Create New Campaign"

        if(sessionStorage.getItem('url').search("signup") === -1){
            this.Dialog = false
        }
        else{
            this.Dialog = true
        }
    },

    methods: {
        goBack() {
            this.$router.push('/savings/campaigns/active');
        },

        proceedToForm() {
            if (this.campaignIntent && this.campaignIntent.trim().length >= 10) {
                // Auto-fill the description field
                this.campaignDescription = this.campaignIntent;

                // Hide intent dialog and show main form
                this.showIntentDialog = false;

                // Send intent to backend for analytics
                this.saveCampaignIntent();
            }
        },

        async saveCampaignIntent() {
            try {
                await axios.post('/api/v1/savings/campaign/intent/', {
                    intent: this.campaignIntent
                });
            } catch (error) {
                console.log("Intent logging failed:", error);
                // Continue even if logging fails
            }
        },

        async getMarketItemInfo() {
            this.$store.commit('setIsLoading', true)
            const product_id = this.$store.state.save_item

            if(product_id !== ""){
                await axios
                    .get('/api/v1/market_place/get/product/details/'+product_id+'/')
                    .then(response => {
                        this.product = response.data["product_info"]
                        this.campaignName = "Saving to buy "+this.product.name
                        this.campaignTargetAmount = this.product.price
                        this.campaignIntent = "I am saving to buy a "+this.product.name+" of amount "+this.product.price+" F CFA"
                        this.campaignDescription = this.campaignIntent
                        this.campaignType = "MONEY_TARGET"

                        this.isAmountDisabled = false
                        this.isDateDisabled = true
                        this.isAmountRequired = false
                        this.isDateRequired = true
                    })
                    .catch(error => {
                        if (error.response){
                            this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                            this.$store.commit('setSnackBarColor', "red darken-4")
                            this.$store.commit('activateSnackBar', true)
                        }else{
                            this.$store.commit('setSnackBarMessage', "An error occured, please check your internet connection and try again later.")
                            this.$store.commit('setSnackBarColor', "red darken-4")
                            this.$store.commit('activateSnackBar', true)
                        }
                    })
            }
            this.$store.commit('setIsLoading', false)
        },

        async getCampaignTypes() {
            this.$store.commit('setIsLoading', true)

            await axios
                .get('/api/v1/savings/get/campaign/types/')
                .then(response => {
                    this.campaignTypes = response.data
                })
                .catch(error => {
                    if (error.response){
                        this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                        this.$store.commit('setSnackBarColor', "red darken-4")
                        this.$store.commit('activateSnackBar', true)
                    }else{
                        this.$store.commit('setSnackBarMessage', "An error occured, please check your internet connection and try again later.")
                        this.$store.commit('setSnackBarColor', "red darken-4")
                        this.$store.commit('activateSnackBar', true)
                    }
                })

            this.$store.commit('setIsLoading', false)
        },

        async createNewCampaign() {
            var future = new Date();
            future.setDate(future.getDate() + 30);

            if(this.campaignName === ""){
                this.$store.commit('setSnackBarMessage', "Please fill in the campaign name")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                this.$refs.campaign_name.focus()
            }
            else if(this.campaignType === ""){
                this.$store.commit('setSnackBarMessage', "Please select the campaign type")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                this.$refs.campaign_type.focus()
            }
            else if ((this.campaignType === "MONEY_TARGET" || this.campaignType === "MONEY_TIME_TARGET") && Number(this.campaignTargetAmount) < 5000){
                this.$store.commit('setSnackBarMessage', "Please your campaign target amount should be from 5000 F CFA and above.")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                this.$refs.campaign_target_amount.focus()
            }
            else if ((this.campaignType === "TIME_TARGET" || this.campaignType === "MONEY_TIME_TARGET") && new Date(this.campaignTargetDate) < future){
                this.$store.commit('setSnackBarMessage', "Sorry your campaign date must be atleast <b>1 month from now</b>.")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                this.$refs.campaign_target_date.focus()
            }
            else if(this.campaignType === "MONEY_TARGET" && this.campaignTargetAmount === ""){
                this.$store.commit('setSnackBarMessage', "Please input your campaign target amount")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                this.$refs.campaign_target_amount.focus()
            }
            else if(this.campaignType === "TIME_TARGET" && this.campaignTargetDate === ""){
                this.$store.commit('setSnackBarMessage', "Please select your campaign target date")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                this.$refs.campaign_target_date.focus()
            }
            else if(this.campaignType === "MONEY_TIME_TARGET" && this.campaignTargetDate === ""){
                this.$store.commit('setSnackBarMessage', "Please select your campaign target date")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                this.$refs.campaign_target_date.focus()
            }
            else if(this.campaignType === "MONEY_TIME_TARGET" && this.campaignTargetAmount === ""){
                this.$store.commit('setSnackBarMessage', "Please input your campaign target amount")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                this.$refs.campaign_target_amount.focus()
            }
            else if(this.campaignDescription === ""){
                this.$store.commit('setSnackBarMessage', "Please input your campaign description")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                this.$refs.campaign_description.focus()
            }
            else {
                this.$store.commit('setIsLoading', true)
                const product_id = this.$store.state.save_item

                await axios
                    .post('/api/v1/savings/create/new_campaign/', {
                        'productID': product_id,
                        'campaignName': this.campaignName,
                        'campaignType': this.campaignType,
                        'campaignTargetAmount': this.campaignTargetAmount,
                        'campaignTargetDate': this.campaignTargetDate,
                        'campaignDescription': this.campaignDescription,
                        'campaignIntent': this.campaignIntent
                    })
                    .then(response => {
                        this.$store.commit('setSnackBarMessage', response.data[0].text)
                        this.$store.commit('setSnackBarColor', response.data[0].color)
                        this.$store.commit('activateSnackBar', true)
                        if(response.data[0].id !== 0){
                            let router = this.$router
                            setTimeout(function(){
                                router.push('/savings/campaigns/detail/'+response.data[0].id+'/')
                            }, 4000)
                        }
                    })
                    .catch(error => {
                        if (error.response){
                            this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                            this.$store.commit('setSnackBarColor', "red darken-4")
                            this.$store.commit('activateSnackBar', true)
                        }else{
                            this.$store.commit('setSnackBarMessage', "An error occured, please check your internet connection and try again later.")
                            this.$store.commit('setSnackBarColor', "red darken-4")
                            this.$store.commit('activateSnackBar', true)
                        }
                    })
                this.$store.commit('setIsLoading', false)
            }
        },

        getHelpMessage() {
            if (this.campaign_name_help){
                this.campaign_name_help = false
                this.$store.commit('setDialogTitle', "Campaign Name")
                this.$store.commit('setDialogMessage', "Here you are required to input your campaign name. This name is usually descriptive of the objective for your savings.")
                this.$store.commit('activateDialog', true)
            }else if (this.campaign_type_help){
                this.campaign_type_help = false
                this.$store.commit('setDialogTitle', "Campaign Type")
                this.$store.commit('setDialogMessage', "You are required to choose your campaign type here. We have 4 types of campaigns, (Periodic, Money and Money-Time Target). If your target is a certain amount, choose the MONEY TARGET option. If your target is certain date then you choose TIME TARGET. If your target is a certain amount at a particular time, then choose MONEY-TIME TARGET. In the case of you having no idea, you can choose UNDEFINED.")
                this.$store.commit('activateDialog', true)
            }else if (this.campaign_amount_help){
                this.campaign_amount_help = false
                this.$store.commit('setDialogTitle', "Campaign Amount")
                this.$store.commit('setDialogMessage', "If you have a certain amount saved as your campaign objective, you will have to input your targeted amount.")
                this.$store.commit('activateDialog', true)
            }else if (this.campaign_date_help){
                this.campaign_date_help = false
                this.$store.commit('setDialogTitle', "Campaign Date")
                this.$store.commit('setDialogMessage', "If you have a certain date saved as your campaign objective, you will have to input your targeted date.")
                this.$store.commit('activateDialog', true)
            }else if (this.campaign_description_help){
                this.campaign_description_help = false
                this.$store.commit('setDialogTitle', "Campaign Description")
                this.$store.commit('setDialogMessage', "This entails for you to input the description of why you are setting the objective based savings campaign.")
                this.$store.commit('activateDialog', true)
            }
        },

        OnChangeCampaignType() {
            if(this.campaignType === "MONEY_TARGET"){
                this.isAmountDisabled = false
                this.isDateDisabled = true
                this.isAmountRequired = false
                this.isDateRequired = true
            }
            else if(this.campaignType === "TIME_TARGET"){
                this.isAmountDisabled = true
                this.isDateDisabled = false
                this.isAmountRequired = true
                this.isDateRequired = false
            }
            else if(this.campaignType === "MONEY_TIME_TARGET"){
                this.isAmountDisabled = false
                this.isDateDisabled = false
                this.isAmountRequired = false
                this.isDateRequired = false
            }
            else if(this.campaignType === "UNDEFINED TARGET"){
                this.isAmountDisabled = true
                this.isDateDisabled = true
                this.isAmountRequired = true
                this.isDateRequired = true
            }
        },
    },
}
</script>